import React, { useState, useEffect, useCallback } from 'react';
import 'leaflet/dist/leaflet.css';
import { useNavigate, useParams } from 'react-router-dom';
import MapComponent from '../Maps/MapComponent';
import ShareIcon from "../../assets/icons/share.svg";
import FavouriteTrue from "../../assets/icons/favouritetrue.svg";
import CheckIcon from "../../assets/icons/checked.svg";
import CopyIcon from "../../assets/icons/copy.svg";
import { EmailShareButton, WhatsappShareButton, FacebookShareButton, XShareButton } from "react-share";
import { EmailIcon, WhatsappIcon, FacebookIcon, XIcon } from "react-share";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import MapIcon from '../../assets/icons/Maps.svg'
import GridIcon from '../../assets/icons/Grid.svg'
import OfficeIcon from '../../assets/icons/OfficeList.svg'
import OtherIcon from '../../assets/icons/OtherList.svg'
import HomeIcon from '../../assets/icons/HomeLIst.svg'
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import { getUserProfile, UserProfile } from '../../API/UserProfileApi/GetUserProfile';
import { RemoveFromFavouritesAsync } from '../../API/AddressFavourites/RemoveFromFavouritesAsync';
import { GetFavouriteAddressesAsync } from '../../API/AddressFavourites/GetFavouriteAddressesAsync';
import { Address } from '../../API/Address/ViewAllAddedAddressByUserAsync';
import LocationIcon from '../../assets/icons/Newmap.svg';
import UnPin from '../../assets/icons/Unpin.svg';
import Pin from '../../assets/icons/Pin.svg';
import { updateLocalStorageSearchHistory } from '../../utils/updateLocalStorageSearchHistory';
import { ClipAndUnClipFavouriteAddressAsync } from "../../API/AddressFavourites/ClipAndUnClipFavouriteAddressAsync";
import { ShareLink } from "../../API/Address/SharePost";
import ReactGA from "react-ga4";
import { useLocalization } from '../Context/MultiLanguageContext';
import { UpdateLabel } from '../../API/AddressFavourites/UpdateLabel';
import { getUserIdFromToken } from '../../auth/authUtils';
import FavFilterPanel from './FavouriteFilterPanel';
import FilterIcon from "../../assets/icons/FilterCircle.svg";

const Favourites: React.FC<{ updateUserName: (newUserName: string) => void }> = ({ updateUserName }) => {
    const navigate = useNavigate();
    const { t } = useLocalization();
    const { id } = useParams<{ id: string }>();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [addresses, setAddresses] = useState<any[]>([]);
    const [viewAsGrid, setViewAsGrid] = useState(false);
    const [addressResults, setAddressResults] = useState<Address[]>([]);
    const [copiedField, setCopiedField] = useState<{ type: "pamCode" | "digiPin"; value: string } | null>(null);

    const [searchData, setSearchData] = useState("");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState("");
    const [activeSharePamCode, setActiveSharePamCode] = useState<string | null>(null);
    const [userName, setUserName] = useState('');
    const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [shareUrls, setShareUrls] = useState<{ [key: string]: { url: string; referralLink?: string } }>({});
    const [isLoadingShare, setIsLoadingShare] = useState<{ [key: string]: boolean }>({});
    const [editingLabel, setEditingLabel] = useState<{ [key: string]: boolean }>({});
    const [addressLabels, setAddressLabels] = useState<{ [key: string]: string }>({});
    const [labelUpdateTimeout, setLabelUpdateTimeout] = useState<{ [key: string]: NodeJS.Timeout }>({});
    const [isGuestUser, setIsGuestUser] = useState<boolean>(false);
    const [selectedAddressType, setSelectedAddressType] = useState("");
    const [selectedPinned, setSelectedPinned] = useState("");
    const [filterSearch, setFilterSearch] = useState("");
    const [sortOrder, setSortOrder] = useState("az");

    const [showFilterPanel, setShowFilterPanel] = useState(false);
    const activeFilterCount =
        (selectedAddressType ? 1 : 0) +
        (selectedPinned ? 1 : 0) +
        (filterSearch ? 1 : 0) +
        (sortOrder ? 1 : 0);


    const handleLabelChange = (addressId: string, value: string) => {
        setAddressLabels(prev => ({ ...prev, [addressId]: value }));

        if (labelUpdateTimeout[addressId]) {
            clearTimeout(labelUpdateTimeout[addressId]);
        }
    };

    const handleSaveLabel = async (addressId: string) => {
        const label = addressLabels[addressId] || '';

        if (label.length > 0 && label.trim() === '') {
            toast.error(t("labelCannotBeEmpty") || "Label cannot contain only spaces");
            return;
        }

        const trimmedLabel = label.trim();

        const token = localStorage.getItem('token');

        if (!token) {
            toast.error(t("sessionExpired"));
            navigate('/');
            return;
        }

        try {
            await UpdateLabel(parseInt(addressId), trimmedLabel, token, t);

            setAddressResults(prev =>
                prev.map(addr =>
                    addr.id === addressId ? { ...addr, label: trimmedLabel } : addr
                )
            );

            setAddressLabels(prev => ({ ...prev, [addressId]: trimmedLabel }));

            setEditingLabel({ ...editingLabel, [addressId]: false });

            ReactGA.event({
                category: "Favourites",
                action: "Updated Address Label",
                label: `Address ID: ${addressId}`,
            });
        } catch (error) {
            console.error('Error updating label:', error);
            toast.error(t("errorUpdatingLabel") || "Error updating label");
        }
    };

    const handleLabelBlur = (addressId: string) => {
        setEditingLabel({ ...editingLabel, [addressId]: false });
    };

    useEffect(() => {
        ReactGA.send({
            hitType: "pageview",
            page: window.location.pathname + window.location.search,
        });
    }, []);

    const handleAddressClick = (address: Address) => {
        ReactGA.event({
            category: "Favourites",
            action: "Clicked Favourite Address",
            label: address.pamCode || address.id,
        });
        const addressForDetail = { ...address, fromFavorites: true };
        navigate(`/favourite-address-details/${address.id}`, { state: { address: addressForDetail } });
    };

    const handleCopy = async (value: string, type: "pamCode" | "digiPin") => {
        try {
            const finalValue = String(value ?? "").trim();
            await navigator.clipboard.writeText(finalValue);
            setCopiedField({ type, value: finalValue });
            setTimeout(() => setCopiedField(null), 2000);
            ReactGA.event({
                category: "AddressList",
                action: `${type === "pamCode" ? "PAM Code" : "DigiPin"} Copied`,
                label: finalValue,
            });
        } catch (err) {
        }
    };

    const HeartClick = async (addressId: number) => {
        const token = localStorage.getItem('token');
        if (!token) {
            return;
        }
        ReactGA.event({
            category: "Favourites",
            action: "Unfavourite Address",
            label: `Address ID: ${addressId}`,
        });

        setAddressResults(prevData =>
            prevData.filter(item => Number(item.id) !== addressId)
        );

        try {
            await RemoveFromFavouritesAsync(addressId, token);
            updateLocalStorageSearchHistory(addressId, false);
        } catch (error) {
        }
    };

    const pinnedList = addressResults.filter(address => address.isClipped);
    const unpinnedList = addressResults.filter(address => !address.isClipped);
    const sortedAddressResults = [...pinnedList, ...unpinnedList];

    const handlePinClick = async (addressId: number) => {
        const token = localStorage.getItem("token");
        if (!token) {
            toast.error(t("sessionExpired"));
            return;
        }

        const address = addressResults.find(item => Number(item.id) === addressId);
        if (!address) {
            return;
        }
        const desiredPinState = !address.isClipped;

        try {
            const response = await ClipAndUnClipFavouriteAddressAsync(addressId, desiredPinState, token, t);
            if (response.message) {
                if (response.message === t("clipAddress")) {
                    if (!toast.isActive("terms-error")) {
                        toast.warn(t("pinaddress"), { toastId: "terms-error" });
                    }
                } else {
                    if (!toast.isActive("terms-error")) {
                        toast.warn(t("pinaddress"), { toastId: "terms-error" });
                    }
                }
            } else {
                ReactGA.event({
                    category: "Favourites",
                    action: desiredPinState ? "Pinned Address" : "Unpinned Address",
                    label: address.pamCode || address.id,
                });
                setAddressResults(prevData =>
                    prevData.map(item =>
                        Number(item.id) === addressId ? { ...item, isClipped: response.isClipped } : item
                    )
                );
            }
        } catch (error) {
            toast.error(t("pinnedStatus"));
        }
    };

    useEffect(() => {
        const storedUserName = localStorage.getItem('userName');
        if (storedUserName) {
            setUserName(storedUserName);
        }
    }, []);

    const fetchAndHandleUserProfile = async () => {
        try {
            const data = await getUserProfile(t);
            setUserProfile(data);
            setUserName(data.userName);
            updateUserName(data.userName);
            navigate('/profile', {
                state: {
                    userName: data.userName,
                    email: data.email,
                    phoneNumber: data.phoneNumber,
                },
            });
        } catch (error) {
            toast.error(t("sessionExpired"));
            navigate('/home');
        }
    };

    const handleShareClick = async (pamCode: string, addressId: string) => {
        ReactGA.event({
            category: "Favourites",
            action: "Share Favourite Address",
            label: pamCode,
        });

        if (activeSharePamCode === pamCode) {
            setActiveSharePamCode(null);
            return;
        }

        setIsLoadingShare(prev => ({ ...prev, [addressId]: true }));

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                toast.error(t("sessionExpired"));
                navigate('/');
                return;
            }

            const shareResponse = await ShareLink(parseInt(addressId), token);

            if (shareResponse && shareResponse.url) {
                setShareUrls(prev => ({
                    ...prev,
                    [addressId]: {
                        url: shareResponse.url,
                        referralLink: shareResponse.referralLink
                    }
                }));
                setActiveSharePamCode(pamCode);
            } else {
                toast.error(t("errorGeneratingShareLink"));
            }
        } catch (error) {
            console.error('Error calling share API:', error);
            toast.error(t("errorGeneratingShareLink"));
        } finally {
            setIsLoadingShare(prev => ({ ...prev, [addressId]: false }));
        }
    };

    const handleLocationIconClick = (latitude: number, longitude: number) => {
        ReactGA.event({
            category: "Favourites",
            action: "Clicked Location Icon",
            label: `Lat: ${latitude}, Lng: ${longitude}`,
        });
        const defaultMap = localStorage.getItem("defaultMap") || "google";

        if (defaultMap === "waze") {
            const wazeMapsUrl = `https://waze.com/ul?ll=${latitude},${longitude}&navigate=yes`;
            window.open(wazeMapsUrl, '_blank');
        } else if (defaultMap === "mapindia") {
            const mapplsUrl = `https://www.mappls.com/@${latitude},${longitude}`;
            window.open(mapplsUrl, "_blank");
        } else if (defaultMap === "apple") {
            const appleMapsUrl = `https://maps.apple.com/?ll=${latitude},${longitude}&q=${latitude},${longitude}&z=18&t=m`;
            window.open(appleMapsUrl, "_blank");
        } else {
            const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
            window.open(googleMapsUrl, '_blank');
        }
    };

    const formatPamCode = (pamCode: string): string => {
        if (!pamCode) return "N/A";

        if (pamCode.includes("-")) return pamCode;

        if (pamCode.length === 10) {
            const pincode = pamCode.slice(0, 6);
            const blockCode = pamCode.slice(6, 10);
            return `${pincode}-${blockCode}`;
        }

        if (pamCode.length >= 11) {
            const pincode = pamCode.slice(0, 6);
            const blockCode = pamCode.slice(6, 10);
            const addressCode = pamCode.slice(10);
            return `${pincode}-${blockCode}-${addressCode}`;
        }

        return pamCode;
    };

    function formatPamCodeForCopy(address: any): string {
        const pin = String(address?.pinCode ?? "").trim();
        const block = String(address?.blockCode ?? "").trim();
        const addr = String(address?.addressCode ?? "").trim();
        if (!pin || !block || !addr) return "N/A";
        return `${pin}-${block}-${addr}`;
    }

    const getDisplayLabel = (addr: any): string => {
        const rawLabel = addressLabels[addr.id] !== undefined ? addressLabels[addr.id] : addr.label || '';
        return rawLabel.trim();
    };

    const shareMessage = (address: Address): string => {
        const rawPamCode =
            address.pamCode && address.pamCode.trim().length > 0
                ? address.pamCode
                : `${address.pinCode}${address.blockCode}${address.addressCode}`;

        const formattedPamCode = formatPamCode(rawPamCode);
        const shareData = shareUrls[address.id];
        const dynamicUrl = shareData?.url;
        const referralLink = shareData?.referralLink;
        const fallbackUrl = `https://app.getmethere.in/${formatPamCodeForCopy(address)}`;

        const addressLink = dynamicUrl || fallbackUrl;

        const isPreprod = process.env.REACT_APP_API_BASE_URL === "https://preprod-api.getmethere.in";
        const firstName = (userName || "").trim().split(/\s+/)[0] || "";
        const userId = getUserIdFromToken();
        const userRefLink = referralLink
            ? referralLink
            : (isPreprod
                ? `https://preprod.getmethere.in/?ref=${userId || ""}&by=${firstName}`
                : `https://getmethere.in/?ref=${userId || ""}&by=${firstName}`);

        return `Check out this address:
  ${address.addressLine1}, ${address.addressLine2}, ${address.addressLine3}
  PAM Code: ${formattedPamCode}
  Digi Pin: ${address?.digiPin || "N/A"}
  Address Link: ${addressLink}

  More details: ${userRefLink}`;
    };

    const toggleDropdown = () => {
        setDropdownOpen(!dropdownOpen);
    };

    const fetchAddresses = async () => {
        const token = localStorage.getItem('token');

        if (!token) {
            toast.error(t("sessionExpired"));
            navigate('/');
            return;
        }

        try {
            const data = await GetFavouriteAddressesAsync(token, t);

            if (data === null) {
                return;
            }

            setAddresses(data);
            setAddressResults(data);
        } catch (error) {
            toast.error(t("unexpectedError"));
        } finally {
            setLoading(false);
            setIsLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('userToken');
        localStorage.removeItem('authToken');
        localStorage.removeItem('token');
        localStorage.removeItem('userName');

        sessionStorage.clear();

        navigate("/", { replace: true });
    };

    useEffect(() => {
        fetchAddresses();
        checkIfGuestUser();
    }, []);

    const checkIfGuestUser = async () => {
        try {
            const profile = await getUserProfile(t);
            setIsGuestUser(profile.email === 'guest@getmethere.in');
        } catch (error) {
            setIsGuestUser(false);
        }
    };

    useEffect(() => {
        let filtered = [...addresses];

        if (filterSearch) {
            filtered = filtered.filter(address =>
                address.addressLine1?.toLowerCase().includes(filterSearch.toLowerCase()) ||
                address.addressLine2?.toLowerCase().includes(filterSearch.toLowerCase()) ||
                address.addressLine3?.toLowerCase().includes(filterSearch.toLowerCase()) ||
                address.pinCode?.toLowerCase().includes(filterSearch.toLowerCase()) ||
                address.label?.toLowerCase().includes(filterSearch.toLowerCase())
            );
        }

        if (selectedAddressType) {
            filtered = filtered.filter(
                address =>
                    address.addressType?.toLowerCase() ===
                    selectedAddressType.toLowerCase()
            );
        }

        if (selectedPinned === "Pinned") {
            filtered = filtered.filter(address => address.isClipped);
        }

        if (selectedPinned === "Unpinned") {
            filtered = filtered.filter(address => !address.isClipped);
        }

        if (sortOrder === "az") {
            filtered.sort((a, b) =>
                (a.addressLine1 || "").localeCompare(b.addressLine1 || "")
            );
        }

        if (sortOrder === "za") {
            filtered.sort((a, b) =>
                (b.addressLine1 || "").localeCompare(a.addressLine1 || "")
            );
        }

        setAddressResults(filtered);
    }, [
        addresses,
        filterSearch,
        selectedAddressType,
        selectedPinned,
        sortOrder
    ]);

    const initialPosition = addressResults.length > 0
        ? [addressResults[0].latitude, addressResults[0].longitude]
        : [40.7128, -74.0060];

    const FavouriteSkeleton: React.FC = () => {
        return (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-lg bg-white shadow-shadow1 w-auto p-4 mb-14">
                {Array.from({ length: 6 }).map((_, index) => (
                    <div key={index} className="p-4 border rounded-md animate-pulse">
                        <div className="h-4 w-1/2 bg-gray-300 mb-2"></div>
                        <div className="h-4 w-full bg-gray-300 mb-2"></div>
                        <div className="h-4 w-3/4 bg-gray-300 mb-2"></div>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="bg-slate-100 flex flex-col justify-between min-h-screen relative min-w-60">
            <ToastContainer />
            <Header userName={userName} fetchUserProfile={fetchAndHandleUserProfile} handleLogout={handleLogout} />
            <main className="relative flex-1 mt-6 px-4 space-y-4 mb-8">

                <div className="flex justify-between items-center mb-4 sm:mb-0">
                    <div className="flex items-center gap-4">
                        <h1 className="text-base sm:text-lg text-dark font-semibold">
                            {t("favourites")}
                        </h1>

                        <div className="rounded-lg shadow-shadow1 sm:w-auto w-full border bg-white border-white">
                            <ul
                                className="relative flex flex-wrap px-2.5 py-3.5 list-none rounded-md bg-white"
                                data-tabs="tabs"
                                role="list"
                            >
                                <li className="flex-auto text-center">
                                    <a
                                        className={`flex items-center justify-center w-full px-4 py-2 text-sm mb-0 transition-all ease-in-out border border-white rounded-md cursor-pointer ${!viewAsGrid
                                            ? "border-white text-blue-500 bg-[#D6DFFE]"
                                            : "text-slate-600 bg-inherit"
                                            }`}
                                        role="tab"
                                        onClick={() => setViewAsGrid(false)}
                                    >
                                        <img
                                            src={GridIcon}
                                            alt="View as Grid"
                                            className="w-4 h-4 mr-1.5"
                                        />
                                        <span className="ml-1">{t("details")}</span>
                                    </a>
                                </li>

                                <li className="flex-auto text-center mr-2">
                                    <a
                                        className={`flex items-center justify-center w-full px-4 py-2 text-sm mb-0 transition-all ease-in-out border border-white rounded-md cursor-pointer ${viewAsGrid
                                            ? "border-white text-blue-500 bg-[#D6DFFE]"
                                            : "text-slate-600 bg-inherit"
                                            }`}
                                        role="tab"
                                        onClick={() => setViewAsGrid(true)}
                                    >
                                        <img
                                            src={MapIcon}
                                            alt="View on Map"
                                            className="w-4 h-4 mr-1.5"
                                        />
                                        <span className="ml-1">{t("map")}</span>
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="relative">
                        <button
                            onClick={() => setShowFilterPanel(!showFilterPanel)}
                            className="flex items-center px-4 py-2 border border-blue-500 bg-white text-blue-500 rounded-md shadow-md hover:bg-blue-500 hover:text-white transition-colors duration-300 transform hover:scale-105 relative"
                        >
                            <img
                                src={FilterIcon}
                                alt="Filter"
                                className="w-4 h-4 sm:w-6 sm:h-6 mr-1"
                            />
                            <span>{addressResults.length} {t("items")}</span>
                        </button>

                        {activeFilterCount > 0 && (
                            <span className="absolute -top-2 -right-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
                                {activeFilterCount}
                            </span>
                        )}
                    </div>
                </div>

                {showFilterPanel && (
                    <FavFilterPanel
                        selectedAddressType={selectedAddressType}
                        setSelectedAddressType={setSelectedAddressType}
                        selectedPinned={selectedPinned}
                        setSelectedPinned={setSelectedPinned}
                        filterSearch={filterSearch}
                        setFilterSearch={setFilterSearch}
                        sortOrder={sortOrder}
                        setSortOrder={setSortOrder}
                        onClose={() => setShowFilterPanel(false)}
                    />
                )}

                {errorMessage && <div className="text-red-500">{errorMessage}</div>}
                {successMessage && <div className="text-green-500">{successMessage}</div>}

                {isLoading ? (
                    <FavouriteSkeleton />
                ) : !viewAsGrid ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 rounded-lg bg-white shadow-shadow1 w-auto p-4 mb-14">
                        {sortedAddressResults.length > 0 ? (
                            sortedAddressResults.map((address, index) => {
                                const rawPamCode = `${address.pinCode}${address.blockCode}${address.addressCode}`;
                                const formattedPamCode = formatPamCode(rawPamCode);

                                return (
                                    <div
                                        key={address.id || index}
                                        className="relative border p-3 rounded-md shadow-sm cursor-pointer hover:border-blue-500"
                                        onClick={(e) => {
                                            const selectedText = window.getSelection()?.toString();

                                            if (selectedText) return;

                                            const target = e.target as HTMLElement;

                                            if (!target.closest("button")) {
                                                handleAddressClick(address);
                                            }
                                        }}
                                    >
                                        <div className='flex flex-col gap-2 mb-3 sm:flex-row sm:justify-between sm:items-start sm:mb-3'>
                                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-2 sm:flex-wrap">
                                                <div className="text-sm gap-2 md:text-base font-medium text-gray-700 flex items-center">
                                                    {address.addressType.toLowerCase() === 'office' && (
                                                        <>
                                                            <img
                                                                src={OfficeIcon}
                                                                alt="Office Icon"
                                                                className="w-6 h-6 sm:w-10 sm:h-10 mr-2"
                                                            />
                                                            <span className="text-xs sm:text-base">{t("office")}</span>
                                                        </>
                                                    )}
                                                    {address.addressType.toLowerCase() === 'home' && (
                                                        <>
                                                            <img
                                                                src={HomeIcon}
                                                                alt="Home Icon"
                                                                className="w-6 h-6 sm:w-10 sm:h-10 mr-2"
                                                            />
                                                            <span className="text-xs sm:text-base">{t("home")}</span>
                                                        </>
                                                    )}
                                                    {address.addressType.toLowerCase() === 'other' && (
                                                        <>
                                                            <img
                                                                src={OtherIcon}
                                                                alt="Other Icon"
                                                                className="w-5 h-5 sm:w-10 sm:h-10 mr-2"
                                                            />
                                                            <span className="text-xs sm:text-base">{t("other")}</span>
                                                        </>
                                                    )}
                                                    {!['office', 'home', 'other'].includes(address.addressType.toLowerCase()) && (
                                                        <>
                                                            <img
                                                                src={OtherIcon}
                                                                alt="Custom Type Icon"
                                                                className="w-6 h-6 sm:w-10 sm:h-10 mr-2"
                                                            />
                                                            <span className="text-xs sm:text-base">{address.addressType}</span>
                                                        </>
                                                    )}
                                                </div>

                                                {!isGuestUser && (
                                                    <div className="hidden sm:block">
                                                        {!editingLabel[address.id] && getDisplayLabel(address) ? (
                                                            <div
                                                                className="flex items-center gap-1 bg-blue-100 px-2 py-1 rounded-full cursor-pointer hover:bg-blue-200 transition-colors"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setEditingLabel({ ...editingLabel, [address.id]: true });
                                                                }}
                                                            >
                                                                <span className="text-xs text-blue-700 font-medium">
                                                                    {getDisplayLabel(address)}
                                                                </span>
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-blue-700" viewBox="0 0 20 20" fill="currentColor">
                                                                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                                                </svg>
                                                            </div>
                                                        ) : !editingLabel[address.id] ? (
                                                            <div
                                                                className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded-full border border-dashed border-gray-300 cursor-pointer hover:bg-gray-200 transition-colors"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setEditingLabel({ ...editingLabel, [address.id]: true });
                                                                }}
                                                            >
                                                                <span className="text-xs text-gray-500 font-medium">
                                                                    {t("addLabel") || "Add label"}
                                                                </span>
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                                                                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                                                </svg>
                                                            </div>
                                                        ) : null}

                                                        {editingLabel[address.id] && (
                                                            <div className="flex items-center gap-1">
                                                                <input
                                                                    type="text"
                                                                    value={addressLabels[address.id] !== undefined ? addressLabels[address.id] : (address.label || '')}
                                                                    maxLength={25}
                                                                    onChange={(e) => {
                                                                        e.stopPropagation();
                                                                        handleLabelChange(address.id, e.target.value);
                                                                    }}
                                                                    onFocus={(e) => e.stopPropagation()}
                                                                    onBlur={(e) => {
                                                                        e.stopPropagation();
                                                                        setTimeout(() => {
                                                                            if (!e.relatedTarget?.closest(`[data-save-btn="${address.id}"]`)) {
                                                                                setEditingLabel({ ...editingLabel, [address.id]: false });
                                                                            }
                                                                        }, 100);
                                                                    }}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter') {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            handleSaveLabel(address.id);
                                                                        }
                                                                    }}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    placeholder={t("addLabel") || "Add label"}
                                                                    className="w-20 px-2 py-1 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
                                                                    autoFocus
                                                                />
                                                                <button
                                                                    data-save-btn={address.id}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleSaveLabel(address.id);
                                                                    }}
                                                                    disabled={!!(addressLabels[address.id] && addressLabels[address.id].trim() === '')}
                                                                    className="w-6 h-6 flex items-center justify-center rounded-md bg-blue-600 text-white text-xs hover:bg-blue-700 focus:outline-none flex-shrink-0 disabled:bg-gray-400 disabled:cursor-not-allowed"
                                                                    title={t("save") || "Save"}
                                                                >
                                                                    ✓
                                                                </button>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setEditingLabel({ ...editingLabel, [address.id]: false });
                                                                        const trimmedOriginalLabel = (address.label || '').trim();
                                                                        if (trimmedOriginalLabel) {
                                                                            setAddressLabels(prev => ({ ...prev, [address.id]: trimmedOriginalLabel }));
                                                                        } else {
                                                                            setAddressLabels(prev => {
                                                                                const next = { ...prev };
                                                                                delete next[address.id];
                                                                                return next;
                                                                            });
                                                                        }
                                                                    }}
                                                                    className="w-6 h-6 flex items-center justify-center rounded-md bg-gray-200 text-gray-700 text-xs hover:bg-gray-300 focus:outline-none flex-shrink-0"
                                                                    title={t("cancel") || "Cancel"}
                                                                >
                                                                    ✕
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="flex gap-1 sm:gap-2 flex-shrink-0 absolute top-3 right-3 sm:relative sm:top-0 sm:right-0">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleShareClick(rawPamCode, address.id);
                                                    }}
                                                    title="Share"
                                                    disabled={isLoadingShare[address.id]}
                                                    className={`p-1 sm:p-1.5 border border-[#D6DFFE] bg-blue-50 rounded-md hover:bg-[#c4d4fc] focus:outline-none flex-shrink-0 ${isLoadingShare[address.id] ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                >
                                                    {isLoadingShare[address.id] ? (
                                                        <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                                    ) : (
                                                        <img src={ShareIcon} alt="Share" className="w-3 h-3 sm:w-4 sm:h-4" />
                                                    )}
                                                </button>

                                                {activeSharePamCode === rawPamCode && (
                                                    <div className="absolute top-8 right-0 z-20 p-2 sm:p-4 bg-white shadow-lg rounded-lg border border-gray-200 flex gap-2 sm:gap-4 mt-2 transform scale-90 sm:scale-105">
                                                        <EmailShareButton url={shareMessage(address)} className="transform transition-transform duration-300 hover:scale-125 hover:shadow-xl">
                                                            <EmailIcon size={24} round className="sm:w-8 sm:h-8" />
                                                        </EmailShareButton>
                                                        <WhatsappShareButton url={shareMessage(address)} className="transform transition-transform duration-300 hover:scale-125 hover:shadow-xl">
                                                            <WhatsappIcon size={24} round className="sm:w-8 sm:h-8" />
                                                        </WhatsappShareButton>
                                                        <FacebookShareButton url={shareMessage(address)} className="transform transition-transform duration-300 hover:scale-125 hover:shadow-xl">
                                                            <FacebookIcon size={24} round className="sm:w-8 sm:h-8" />
                                                        </FacebookShareButton>
                                                        <XShareButton url={shareMessage(address)} className="transform transition-transform duration-300 hover:scale-125 hover:shadow-xl">
                                                            <XIcon size={24} round className="sm:w-8 sm:h-8" />
                                                        </XShareButton>
                                                    </div>
                                                )}

                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        HeartClick(Number(address.id));
                                                    }}
                                                    title="Remove from Favourites"
                                                    className="p-1 sm:p-1.5 border border-[#D6DFFE] bg-red-50 rounded-md hover:bg-[#c4d4fc] focus:outline-none flex-shrink-0"
                                                >
                                                    <img
                                                        src={FavouriteTrue}
                                                        alt="Favourites"
                                                        className="w-3 h-3 sm:w-4 sm:h-4"
                                                    />
                                                </button>

                                                <button
                                                    title="Location"
                                                    className="p-1 sm:p-1.5 border border-[#D6DFFE] bg-gray-100 rounded-md hover:bg-[#c4d4fc] focus:outline-none flex-shrink-0"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleLocationIconClick(address.latitude, address.longitude);
                                                    }}
                                                >
                                                    <img
                                                        src={LocationIcon}
                                                        alt="Location"
                                                        className="h-3 w-3 sm:h-4 sm:w-4 cursor-pointer"
                                                    />
                                                </button>

                                                <button
                                                    title="Pin"
                                                    className="p-1 sm:p-1.5 border border-[#D6DFFE] bg-[#D6DFFE] rounded-md hover:bg-[#c4d4fc] focus:outline-none flex-shrink-0"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handlePinClick(Number(address.id));
                                                    }}
                                                >
                                                    <img
                                                        src={address.isClipped ? Pin : UnPin}
                                                        alt="Pin"
                                                        className="w-3 h-3 sm:w-4 sm:h-4"
                                                        style={{
                                                            filter: address.isClipped
                                                                ? 'brightness(0)'
                                                                : 'grayscale(100%)',
                                                        }}
                                                    />
                                                </button>
                                            </div>

                                            {!isGuestUser && (
                                                <div className="w-full sm:hidden pl-8 -mt-1">
                                                    {!editingLabel[address.id] && getDisplayLabel(address) ? (
                                                        <div
                                                            className="flex items-center gap-0.5 bg-blue-100 px-1.5 py-0.5 rounded-full cursor-pointer hover:bg-blue-200 transition-colors w-fit"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setEditingLabel({ ...editingLabel, [address.id]: true });
                                                            }}
                                                        >
                                                            <span className="text-[10px] text-blue-700 font-medium">
                                                                {getDisplayLabel(address)}
                                                            </span>
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-2.5 w-2.5 text-blue-700" viewBox="0 0 20 20" fill="currentColor">
                                                                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                                            </svg>
                                                        </div>
                                                    ) : !editingLabel[address.id] ? (
                                                        <div
                                                            className="flex items-center gap-0.5 bg-gray-100 px-1.5 py-0.5 rounded-full border border-dashed border-gray-300 cursor-pointer hover:bg-gray-200 transition-colors w-fit"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setEditingLabel({ ...editingLabel, [address.id]: true });
                                                            }}
                                                        >
                                                            <span className="text-[10px] text-gray-500 font-medium">
                                                                {t("addLabel") || "Add label"}
                                                            </span>
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-2.5 w-2.5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                                                                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                                            </svg>
                                                        </div>
                                                    ) : null}

                                                    {editingLabel[address.id] && (
                                                        <div className="flex items-center gap-1">
                                                            <input
                                                                type="text"
                                                                value={addressLabels[address.id] !== undefined ? addressLabels[address.id] : (address.label || '')}
                                                                onChange={(e) => {
                                                                    e.stopPropagation();
                                                                    handleLabelChange(address.id, e.target.value);
                                                                }}
                                                                onFocus={(e) => e.stopPropagation()}
                                                                onBlur={(e) => {
                                                                    e.stopPropagation();
                                                                    setTimeout(() => {
                                                                        if (!e.relatedTarget?.closest(`[data-save-btn="${address.id}"]`)) {
                                                                            setEditingLabel({ ...editingLabel, [address.id]: false });
                                                                        }
                                                                    }, 100);
                                                                }}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        handleSaveLabel(address.id);
                                                                    }
                                                                }}
                                                                onClick={(e) => e.stopPropagation()}
                                                                placeholder={t("addLabel") || "Add label"}
                                                                className="w-20 px-2 py-1 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
                                                                autoFocus
                                                            />
                                                            <button
                                                                data-save-btn={address.id}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleSaveLabel(address.id);
                                                                }}
                                                                disabled={!!(addressLabels[address.id] && addressLabels[address.id].trim() === '')}
                                                                className="w-6 h-6 flex items-center justify-center rounded-md bg-blue-600 text-white text-xs hover:bg-blue-700 focus:outline-none flex-shrink-0 disabled:bg-gray-400 disabled:cursor-not-allowed"
                                                                title={t("save") || "Save"}
                                                            >
                                                                ✓
                                                            </button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setEditingLabel({ ...editingLabel, [address.id]: false });
                                                                    const trimmedOriginalLabel = (address.label || '').trim();
                                                                    if (trimmedOriginalLabel) {
                                                                        setAddressLabels(prev => ({ ...prev, [address.id]: trimmedOriginalLabel }));
                                                                    } else {
                                                                        setAddressLabels(prev => {
                                                                            const next = { ...prev };
                                                                            delete next[address.id];
                                                                            return next;
                                                                        });
                                                                    }
                                                                }}
                                                                className="w-6 h-6 flex items-center justify-center rounded-md bg-gray-200 text-gray-700 text-xs hover:bg-gray-300 focus:outline-none flex-shrink-0"
                                                                title={t("cancel") || "Cancel"}
                                                            >
                                                                ✕
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>



                                        <p className="text-xs sm:text-sm font-semibold text-gray-700 mt-2 break-words select-text">
                                            {address.addressLine1}
                                        </p>

                                        <p className="text-xs sm:text-sm text-[#61677D] break-words mb-2 select-text">
                                            {address.addressLine2}, {address.addressLine3}, {address.postOfficeName} P.O.
                                        </p>

                                        <div className='flex gap-3 mt-2 relative flex-col w-full'>
                                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-2 w-full">
                                                <label htmlFor="pamCode" className="text-xs sm:text-sm font-medium whitespace-nowrap w-full sm:w-24">
                                                    {t("pamCode")}:
                                                </label>
                                                <div className="relative flex-1">
                                                    <input
                                                        type="text"
                                                        className="w-full p-1.5 sm:p-2 pr-8 sm:pr-10 border rounded-md text-xs sm:text-sm"
                                                        value={formattedPamCode}
                                                        readOnly
                                                        onClick={(e) => e.stopPropagation()}
                                                        onMouseDown={(e) => e.stopPropagation()}
                                                    />
                                                    <button
                                                        className="absolute right-1 sm:right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-blue-500"
                                                        onClick={() => handleCopy(formattedPamCode, "pamCode")}
                                                    >
                                                        {copiedField?.type === "pamCode" && copiedField.value === formattedPamCode ? (
                                                            <img src={CheckIcon} alt="Copied" className="w-3 h-3 sm:w-4 sm:h-4" />
                                                        ) : (
                                                            <img src={CopyIcon} alt="Copy" className="w-3 h-3 sm:w-4 sm:h-4" />
                                                        )}
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-2 w-full mb-2 mt-2">
                                                <label htmlFor="digipin" className="text-xs sm:text-sm font-medium whitespace-nowrap w-full sm:w-24">
                                                    {t("digiPin")}:
                                                </label>
                                                <div className="relative flex-1">
                                                    <input
                                                        type="text"
                                                        className="w-full p-1.5 sm:p-2 pr-8 sm:pr-10 border rounded-md text-xs sm:text-sm"
                                                        value={address.digiPin}
                                                        readOnly
                                                        onClick={(e) => e.stopPropagation()}
                                                        onMouseDown={(e) => e.stopPropagation()}
                                                    />
                                                    <button
                                                        className="absolute right-1 sm:right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-blue-500"
                                                        onClick={() => handleCopy(address.digiPin, "digiPin")}
                                                    >
                                                        {copiedField?.type === "digiPin" && copiedField.value === address.digiPin ? (
                                                            <img src={CheckIcon} alt="Copied" className="w-3 h-3 sm:w-4 sm:h-4" />
                                                        ) : (
                                                            <img src={CopyIcon} alt="Copy" className="w-3 h-3 sm:w-4 sm:h-4" />
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <p className="text-gray-500"> {t("addressFavourites")}</p>
                        )}
                    </div>
                ) : (
                    <div className="p-3 mx-auto bg-white m-0 rounded-lg h-500px w-[100%]">
                        <div className="w-full overflow-hidden" style={{ height: 'calc(100vh - (300px))' }}>
                            <MapComponent
                                addresses={addressResults}
                                showLocateControl={false}
                            />                        </div>
                    </div>
                )}
            </main>
            <Footer />
        </div>
    );
};

export default Favourites;